// packages/api/src/hooks/useCepLookup.ts

import { useQuery } from '@tanstack/react-query';

import type { CepLookupResult } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

/** Cache do proxy de CEP — o backend já guarda 30d em Valkey. */
export const CEP_LOOKUP_STALE_TIME_MS = 30 * 60 * 1000;

/** Normaliza para os 8 dígitos aceitos pelo proxy; string vazia quando inválido. */
export function normalizeCep(cep: string | null | undefined): string {
  const digits = String(cep ?? '').replace(/\D/g, '');
  return digits.length === 8 ? digits : '';
}

/**
 * Busca imperativa do proxy de CEP (GET /registrations/cep/:cep) — mesma rota e
 * mesma chave de cache do `useCepLookup`, para quem precisa resolver um CEP
 * dentro de um handler assíncrono (ex.: aplicar N endereços de uma vez) em vez
 * de dentro do render.
 */
export async function fetchCepLookup(
  cep: string,
  options?: { signal?: AbortSignal },
): Promise<CepLookupResult> {
  const response = await apiClient.get<{ data: CepLookupResult }>(`/registrations/cep/${cep}`, {
    signal: options?.signal,
  });
  return response.data.data;
}

/**
 * Fetch address data by CEP through the backend proxy.
 *
 * Backend (GET /api/v1/registrations/cep/:cep) cascades BrasilAPI v2 → ViaCEP,
 * caches the result in Valkey for 30d, and resolves `cityId` via LocationsService
 * (IBGE match, with findCityByNameAndUf fallback).
 *
 * Returns `null` when the input is not a valid 8-digit CEP (keeps the hook
 * safe to call eagerly during form state transitions). Shared across the whole
 * app (quotation, profile, party, registration, block conference) — a general
 * address proxy, not registration-specific.
 */
export function useCepLookup(cep: string) {
  const cleanCep = (cep ?? '').replace(/\D/g, '');
  return useQuery({
    queryKey: queryKeys.cep.lookup(cleanCep),
    // `signal` is wired through to axios so that when the user edits the CEP
    // the in-flight request is aborted before a new one is fired — prevents
    // stale responses from overwriting a fresher cached result.
    queryFn: ({ signal }) => fetchCepLookup(cleanCep, { signal }),
    enabled: cleanCep.length === 8,
    staleTime: CEP_LOOKUP_STALE_TIME_MS,
    retry: 1,
  });
}
